import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Calendar, Clock, MapPin, MessageSquare, Phone, Mail, Edit, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { toast } from "sonner";

interface AppointmentDetailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  appointment: any;
  onEdit?: (appointment: any) => void;
  onCancel?: (appointment: any) => void;
}

export function AppointmentDetailDialog({ 
  isOpen, 
  onClose, 
  appointment, 
  onEdit, 
  onCancel 
}: AppointmentDetailDialogProps) {
  if (!appointment) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800 border-green-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'completed': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'confirmed': return 'Bevestigd';
      case 'pending': return 'In afwachting';
      case 'completed': return 'Voltooid';
      case 'cancelled': return 'Geannuleerd';
      default: return status;
    }
  };

  const handleContact = () => {
    toast.info(`Contact opnemen met ${appointment.providerName}`, {
      description: "Email, telefoon of chat beschikbaar",
      duration: 4000
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md mx-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-navy-800 text-center">
            Afspraak Details
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Provider Info */}
          <div className="flex items-center gap-4 bg-cream-50 rounded-lg p-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={appointment.providerAvatar} alt={appointment.providerName} />
              <AvatarFallback className="bg-cream-200 text-navy-700 text-lg">
                {appointment.providerName?.split(' ').map((n: string) => n[0]).join('') || 'WP'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-navy-800">{appointment.serviceTitle}</h3>
              <p className="text-navy-600">met {appointment.providerName}</p>
              <Badge className={getStatusColor(appointment.status || 'confirmed')}>
                {getStatusText(appointment.status || 'confirmed')}
              </Badge>
            </div>
          </div>

          {/* Appointment Details */}
          <div className="space-y-4 bg-cream-50 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-start gap-2 text-navy-700">
                <Calendar className="h-5 w-5 mt-0.5" />
                <div>
                  <p className="font-semibold">Datum</p>
                  <p className="text-navy-600">{format(appointment.date, 'eeee d MMMM yyyy', { locale: nl })}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-2 text-navy-700">
                <Clock className="h-5 w-5 mt-0.5" />
                <div>
                  <p className="font-semibold">Tijd</p>
                  <p className="text-navy-600">{appointment.time}</p>
                  <p className="text-xs text-navy-500">({appointment.duration || '60 min'})</p>
                </div>
              </div>
              
              <div className="flex items-start gap-2 text-navy-700">
                <MapPin className="h-5 w-5 mt-0.5" />
                <div>
                  <p className="font-semibold">Locatie</p>
                  <p className="text-navy-600">{appointment.location || 'Online'}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-2 text-navy-700">
                <div className="h-5 w-5 mt-0.5 flex items-center justify-center">
                  <span className="text-lg font-bold text-gold-600">€</span>
                </div>
                <div>
                  <p className="font-semibold">Prijs</p>
                  <p className="text-navy-600">€{appointment.price || 0}</p>
                </div>
              </div>
            </div>
            
            {appointment.comments && (
              <div className="bg-white rounded-lg p-3 border border-cream-200">
                <p className="font-medium text-navy-700 mb-1 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Opmerkingen:
                </p>
                <p className="text-navy-600 text-sm leading-relaxed">{appointment.comments}</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="flex-1 btn-elegant-secondary"
              onClick={handleContact}
            >
              <Phone className="h-4 w-4 mr-2" />
              Contact
            </Button>
            
            {onEdit && (
              <Button 
                onClick={() => {
                  onClose();
                  onEdit(appointment);
                }}
                className="flex-1 btn-elegant-primary"
              >
                <Edit className="h-4 w-4 mr-2" />
                Bewerken
              </Button>
            )}
            
            {onCancel && (
              <Button 
                variant="destructive"
                onClick={() => {
                  onClose();
                  onCancel(appointment);
                }}
                className="bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Annuleren
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}